/**
 * AiFeedback - Reusable thumbs up/down component for AI-generated content.
 *
 * Shows thumbs up and thumbs down icons. Thumbs down opens a styled Radix Dialog
 * for free-form feedback. Clicking a previously-submitted thumbs-down re-opens
 * the dialog so users can view/edit their comment. Feedback is editable (users
 * can switch between thumbs up and thumbs down).
 *
 * Submits to POST /residents/{id}/ai-feedback.
 */

import React, { useEffect, useState } from "react";
import { ThumbsUp, ThumbsDown, XCircle } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApiService } from "../../contexts/ApiServiceContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import type {
  AiFeedbackContentType,
  AiFeedbackSentiment,
} from "../../types/residents";
import { trackCareQueueEvent } from "../../lib/tracking";

interface AiFeedbackProps {
  residentId: string;
  contentType: AiFeedbackContentType;
  riskDocumentId: string;
  sourceItemId?: string;
  wqId?: string;
  insightsDays?: number;
  existingSentiment?: AiFeedbackSentiment | null;
  existingComment?: string | null;
}

const CONTENT_TYPE_LABELS: Record<AiFeedbackContentType, string> = {
  ai_summary: "AI Summary",
  note_reasoning: "AI Reasoning",
  medication_classification: "Medication Classification",
  note_risk_classification: "Risk Classification",
};

export const AiFeedback: React.FC<AiFeedbackProps> = ({
  residentId,
  contentType,
  riskDocumentId,
  sourceItemId,
  wqId,
  insightsDays,
  existingSentiment = null,
  existingComment = null,
}) => {
  const api = useApiService();
  const queryClient = useQueryClient();
  const [selectedSentiment, setSelectedSentiment] =
    useState<AiFeedbackSentiment | null>(existingSentiment);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [comment, setComment] = useState(existingComment ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existingSentiment !== null) {
      setSelectedSentiment(existingSentiment);
    }
  }, [existingSentiment]);

  useEffect(() => {
    setComment(existingComment ?? "");
  }, [existingComment]);

  const mutation = useMutation({
    mutationFn: (sentiment: AiFeedbackSentiment) =>
      api.submitAiFeedback(
        residentId,
        {
          content_type: contentType,
          sentiment,
          comment: sentiment === "negative" ? comment : null,
          source_item_id: sourceItemId ?? null,
          risk_document_id: riskDocumentId,
          lookback_period: insightsDays,
        },
        wqId
      ),
    onSuccess: (_data, sentiment) => {
      setSelectedSentiment(sentiment);
      setDialogOpen(false);
      setError(null);
      // Invalidate feedback cache so re-opening the dialog shows fresh state
      queryClient.invalidateQueries({
        queryKey: ["ai-feedback", residentId],
      });
      toast.success("Feedback saved");
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : "Failed to save feedback";
      setError(message);
      toast.error("Failed to save feedback", { description: message });
    },
  });

  const handleThumbsUp = () => {
    if (mutation.isPending) return;
    // If already positive, clicking again is a no-op
    if (selectedSentiment === "positive") return;
    trackCareQueueEvent("care_queue_ai_feedback", {
      rating: "up",
      contextType: contentType,
    });
    setComment("");
    mutation.mutate("positive");
  };

  const handleThumbsDown = () => {
    if (mutation.isPending) return;
    trackCareQueueEvent("care_queue_ai_feedback", {
      rating: "down",
      contextType: contentType,
    });
    // Open dialog for new feedback or to view/edit existing negative feedback
    setError(null);
    setDialogOpen(true);
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      setError(null);
      // Only reset comment if no negative feedback was ever submitted
      if (selectedSentiment !== "negative") {
        setComment("");
      }
    }
    setDialogOpen(open);
  };

  const handleSubmitNegative = () => {
    if (!comment.trim()) {
      setError("Please describe what could be improved");
      return;
    }
    if (comment.trim().length > 2000) {
      setError("Feedback must be 2000 characters or less");
      return;
    }
    setError(null);
    mutation.mutate("negative");
  };

  return (
    <>
      <div className="inline-flex items-center gap-1">
        <button
          onClick={handleThumbsUp}
          disabled={mutation.isPending}
          className={`p-1 rounded transition-colors ${
            selectedSentiment === "positive"
              ? "text-green-600"
              : "text-gray-400 hover:text-green-600"
          } disabled:cursor-not-allowed`}
          title="Helpful"
          aria-label="Thumbs up - AI content was helpful"
        >
          <ThumbsUp
            className="h-4 w-4"
            fill={selectedSentiment === "positive" ? "currentColor" : "none"}
          />
        </button>
        <button
          onClick={handleThumbsDown}
          disabled={mutation.isPending}
          className={`p-1 rounded transition-colors ${
            selectedSentiment === "negative"
              ? "text-red-600"
              : "text-gray-400 hover:text-red-600"
          } disabled:cursor-not-allowed`}
          title={selectedSentiment === "negative" ? "View/edit feedback" : "Not helpful"}
          aria-label={
            selectedSentiment === "negative"
              ? "View or edit negative feedback"
              : "Thumbs down - AI content needs improvement"
          }
        >
          <ThumbsDown
            className="h-4 w-4"
            fill={selectedSentiment === "negative" ? "currentColor" : "none"}
          />
        </button>
        {mutation.isPending && (
          <span className="text-xs text-gray-400 ml-1">Saving...</span>
        )}
      </div>

      {/* Negative Feedback Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent
          className="p-0 gap-0 overflow-hidden bg-card [&>button]:hidden"
          style={{ width: "480px", maxWidth: "calc(100vw - 2rem)" }}
        >
          {/* Teal Header */}
          <div className="px-8 py-5 relative rounded-t bg-primary">
            <button
              onClick={() => handleDialogOpenChange(false)}
              className="absolute top-5 right-5 text-primary-foreground/80 hover:text-primary-foreground transition-colors"
              aria-label="Close"
            >
              <XCircle className="h-6 w-6" />
            </button>
            <DialogTitle
              className="text-primary-foreground"
              style={{ fontSize: "20px", fontWeight: 700 }}
            >
              AI Feedback
            </DialogTitle>
            <DialogDescription className="text-base text-primary-foreground/90 mt-1">
              {CONTENT_TYPE_LABELS[contentType]}
            </DialogDescription>
          </div>

          {/* Form Content */}
          <div className="p-6 space-y-5 bg-card">
            <div className="space-y-2">
              <label
                htmlFor={`feedback-comment-${contentType}-${sourceItemId ?? "main"}`}
                className="text-sm font-medium text-foreground"
              >
                What could be improved?
              </label>
              <Textarea
                id={`feedback-comment-${contentType}-${sourceItemId ?? "main"}`}
                placeholder="Describe what was inaccurate or unhelpful..."
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                  if (error) setError(null);
                }}
                className="min-h-[120px] resize-y"
                style={{ border: "1px solid rgba(209, 210, 219, 1.00)" }}
                aria-invalid={!!error}
                autoFocus
              />
              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className="px-6 py-4 border-t border-border bg-muted/50">
            <Button
              variant="outline"
              onClick={() => handleDialogOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitNegative}
              disabled={mutation.isPending || !comment.trim()}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {mutation.isPending ? "Saving..." : "Submit Feedback"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
